import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Home, Building2, Briefcase, Layers, MapPin, Search, Check,
  ChevronRight, ChevronLeft, Sparkles, Upload, Loader2, X,
  Plus, Minus, Box, Globe2, Hammer, Landmark, Wand2, PenTool,
} from 'lucide-react';
import { toast } from 'sonner';
import { buildTileUrl } from '../lib/tiles';
import GoogleMaps3DPicker from './wizard/GoogleMaps3DPicker';
import { HAS_GOOGLE_MAPS_API_KEY } from '../lib/mapsKey';
import { useChannel } from '../contexts/ChannelContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HAS_GOOGLE_MAPS_KEY = HAS_GOOGLE_MAPS_API_KEY;

// Lucide icon names → component map (mirrors backend `icon` field)
const ICONS = { Home, Building2, Briefcase, Layers, Box };

// Illustrations générées (Gemini) — par code de type de projet
const TYPE_IMAGES = {
  apartment_single: 'https://static.prod-images.emergentagent.com/jobs/c5e81897-ca9c-4f98-8dd9-2b73b08b597d/images/317b2ba14cdd30f8126298c16fd167a2bf9e6c7e0c4f703fe4508f21d390ca09.png',
  house_single: 'https://static.prod-images.emergentagent.com/jobs/c5e81897-ca9c-4f98-8dd9-2b73b08b597d/images/5ececaa57133e64c118b99049ff19eea12a373678463da39488fa45925dad71a.png',
  residence_single: 'https://static.prod-images.emergentagent.com/jobs/c5e81897-ca9c-4f98-8dd9-2b73b08b597d/images/414f6b364100a5b3257d329b8ac499bc044375502a1769938a1a3bc56d3ac8b0.png',
  residence_multi: 'https://static.prod-images.emergentagent.com/jobs/c5e81897-ca9c-4f98-8dd9-2b73b08b597d/images/3fc1f135ba540957506ac43f61ccfb5bf35d9a741936220e96e895193092a0a5.png',
  commercial: 'https://static.prod-images.emergentagent.com/jobs/c5e81897-ca9c-4f98-8dd9-2b73b08b597d/images/58ee5068c708144482c530bfe86eaa6bc9a92884a4b1393f80eb095252a9e90e.png',
};
const MODE_IMAGES = {
  basic: 'https://static.prod-images.emergentagent.com/jobs/c5e81897-ca9c-4f98-8dd9-2b73b08b597d/images/17c4b975b4bb34a3f851d18f0f8863789ea2f9b3d5b3be898e27c901ad76dddc.png',
  advanced: 'https://static.prod-images.emergentagent.com/jobs/c5e81897-ca9c-4f98-8dd9-2b73b08b597d/images/00f02af27609480fcffbe687d0352fd2ceffe7a298e42065e9048f6f7711d056.png',
};

// Catégorie → codes de types autorisés
const RESIDENTIAL_CODES = ['apartment_single', 'house_single', 'residence_single', 'residence_multi', 'other'];
const TERTIARY_CODES = ['commercial', 'other'];

// Custom gold pin marker
const goldMarker = new L.DivIcon({
  className: '',
  html: `<div style="width:18px;height:18px;background:#D9A05B;border-radius:50%;border:3px solid #fff;box-shadow:0 0 18px rgba(217,160,91,0.7)"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

// =====================================================================
// Sub-component: BuildingPicker (Mapbox-aware Leaflet picker)
// =====================================================================
const ClickHandler = ({ onPick }) => {
  useMapEvents({ click(e) { onPick([e.latlng.lat, e.latlng.lng]); } });
  return null;
};

const FlyTo = ({ center }) => {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, 17, { duration: 1.2 }); }, [center, map]);
  return null;
};

const BuildingPicker = ({ value, onChange, theme = 'dark', initialSearch = '' }) => {
  const [search, setSearch] = useState(initialSearch);
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState([]);
  const [tileMode, setTileMode] = useState(HAS_GOOGLE_MAPS_KEY ? 'google_3d' : 'satellite');

  const center = value?.coords || [48.8566, 2.3522];

  const doSearch = async () => {
    if (!search.trim()) return;
    setBusy(true); setHits([]);
    try {
      const r = await axios.get('https://nominatim.openstreetmap.org/search', {
        params: { q: search, format: 'json', limit: 5, addressdetails: 1 },
        headers: {},
        withCredentials: false,
      });
      setHits(r.data || []);
    } catch {
      toast.error('Recherche indisponible — cliquez directement sur la carte');
    } finally { setBusy(false); }
  };

  const provider = tileMode === 'google_3d' ? 'google_maps' : 'mapbox';
  const pick = (lat, lng, address) => {
    onChange({ coords: [parseFloat(lat), parseFloat(lng)], address: address || '', map_provider: provider });
    setHits([]);
  };

  const handleGoogle3DPick = ({ lat, lng }) => {
    onChange({ ...(value || {}), coords: [lat, lng], map_provider: 'google_maps' });
  };

  const tileUrl = tileMode === 'streets' ? buildTileUrl('smooth') : buildTileUrl('dark');

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), doSearch())}
          placeholder="Adresse, ville, place…"
          className="bg-white/5 border-white/15 text-white placeholder:text-white/30 pl-9 pr-20 h-10 text-sm rounded-lg"
          data-testid="picker-search-input"
        />
        <button
          onClick={doSearch}
          disabled={busy}
          className="absolute right-1 top-1 h-8 px-3 text-xs font-medium rounded-md bg-[#D9A05B] text-black hover:bg-[#C78D46] disabled:opacity-40"
          data-testid="picker-search-btn"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Chercher'}
        </button>
      </div>

      {hits.length > 0 && (
        <div className="bg-black/40 border border-white/10 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
          {hits.map((h, i) => (
            <button
              key={i}
              onClick={() => pick(h.lat, h.lon, h.display_name)}
              className="w-full text-left px-3 py-2 text-xs text-white/80 hover:bg-white/5 border-b border-white/5 last:border-0"
              data-testid={`picker-hit-${i}`}
            >
              <p className="font-medium truncate">{h.display_name}</p>
              <p className="text-[10px] text-white/40">{parseFloat(h.lat).toFixed(4)}, {parseFloat(h.lon).toFixed(4)}</p>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold flex-wrap">
        {HAS_GOOGLE_MAPS_KEY && (
          <button
            onClick={() => setTileMode('google_3d')}
            className={`px-2.5 py-1 rounded inline-flex items-center gap-1 ${tileMode==='google_3d' ? 'bg-[#D9A05B] text-black' : 'bg-white/5 text-white/50 hover:text-white/80'}`}
            data-testid="picker-mode-google-3d"
            title="Google Maps Photorealistic 3D Tiles"
          >
            <Globe2 className="w-3 h-3" />
            3D Photoréaliste
          </button>
        )}
        <button
          onClick={() => setTileMode('satellite')}
          className={`px-2.5 py-1 rounded ${tileMode==='satellite' ? 'bg-[#D9A05B] text-black' : 'bg-white/5 text-white/50 hover:text-white/80'}`}
          data-testid="picker-mode-satellite"
        >
          Satellite
        </button>
        <button
          onClick={() => setTileMode('streets')}
          className={`px-2.5 py-1 rounded ${tileMode==='streets' ? 'bg-[#D9A05B] text-black' : 'bg-white/5 text-white/50 hover:text-white/80'}`}
          data-testid="picker-mode-streets"
        >
          Plan
        </button>
        <span className="ml-auto text-[9px] text-white/40 normal-case tracking-normal">
          {tileMode === 'google_3d' ? 'Cliquez sur un bâtiment 3D pour ajuster' : 'Cliquez sur la carte pour ajuster'}
        </span>
      </div>

      <div className="h-[260px] rounded-xl overflow-hidden border border-white/10">
        {tileMode === 'google_3d' ? (
          <GoogleMaps3DPicker
            center={value?.coords || null}
            onPickPosition={handleGoogle3DPick}
          />
        ) : (
          <MapContainer center={center} zoom={value?.coords ? 17 : 5} className="w-full h-full" style={{ background: '#09090B' }}>
            <TileLayer key={tileMode} url={tileUrl} />
            <ClickHandler onPick={(c) => onChange({ ...value, coords: c, map_provider: 'mapbox' })} />
            {value?.coords && <FlyTo center={value.coords} />}
            {value?.coords && <Marker position={value.coords} icon={goldMarker} />}
          </MapContainer>
        )}
      </div>

      {value?.coords && (
        <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-3 py-2 text-xs text-emerald-300 flex items-center gap-2" data-testid="picker-confirmation">
          <Check className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{value.address || `Position: ${value.coords[0].toFixed(4)}, ${value.coords[1].toFixed(4)}`}</span>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Small controls — stepper numérique + toggle
// =====================================================================
const NumStepper = ({ value, onChange, min = 0, max = 9, testid }) => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      onClick={() => onChange(Math.max(min, value - 1))}
      className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 flex items-center justify-center"
      data-testid={`${testid}-minus`}
    >
      <Minus className="w-3 h-3" />
    </button>
    <div className="w-12 text-center bg-white/5 border border-white/10 rounded-lg h-8 flex items-center justify-center font-semibold text-white text-sm">
      <span data-testid={`${testid}-value`}>{value}</span>
    </div>
    <button
      type="button"
      onClick={() => onChange(Math.min(max, value + 1))}
      className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 flex items-center justify-center"
      data-testid={`${testid}-plus`}
    >
      <Plus className="w-3 h-3" />
    </button>
  </div>
);

const ToggleChip = ({ checked, onChange, label, testid }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border text-xs font-medium transition-all ${
      checked
        ? 'bg-[#D9A05B]/15 border-[#D9A05B]/40 text-[#D9A05B]'
        : 'bg-white/5 border-white/10 text-white/55 hover:bg-white/10'
    }`}
    data-testid={testid}
  >
    {checked && <Check className="w-3 h-3" />}
    {label}
  </button>
);

const FieldRow = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 px-3 py-2 bg-white/[0.03] border border-white/10 rounded-lg">
    <p className="text-xs text-white/70">{label}</p>
    {children}
  </div>
);

// Grande carte de choix binaire (Résidentiel/Tertiaire, Neuf/Existant)
const BigChoiceCard = ({ selected, onClick, icon: Icon, title, subtitle, testid }) => (
  <button
    onClick={onClick}
    className={`relative p-5 rounded-2xl border text-left transition-all group ${
      selected
        ? 'bg-[#D9A05B]/12 border-[#D9A05B]/50 shadow-[0_0_30px_rgba(217,160,91,0.12)]'
        : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.07] hover:border-white/25'
    }`}
    data-testid={testid}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-colors ${
      selected ? 'bg-[#D9A05B] text-black' : 'bg-white/5 text-white/55 group-hover:text-white/80'
    }`}>
      <Icon className="w-6 h-6" />
    </div>
    <p className={`text-base font-semibold ${selected ? 'text-[#D9A05B]' : 'text-white'}`}>{title}</p>
    <p className="text-[11px] text-white/45 mt-1 leading-relaxed">{subtitle}</p>
    {selected && <Check className="w-5 h-5 text-[#D9A05B] absolute top-4 right-4" />}
  </button>
);

// Carte type de projet avec illustration
const TypeImageCard = ({ type, selected, onClick, renderIcon }) => {
  const img = TYPE_IMAGES[type.code];
  return (
    <button
      onClick={onClick}
      className={`relative rounded-2xl border overflow-hidden text-left transition-all group ${
        selected
          ? 'border-[#D9A05B]/60 shadow-[0_0_30px_rgba(217,160,91,0.15)]'
          : 'border-white/10 hover:border-white/30'
      }`}
      data-testid={`wizard-type-${type.code}`}
    >
      {img ? (
        <div className="h-28 w-full overflow-hidden bg-black">
          <img
            src={img}
            alt={type.label}
            loading="lazy"
            className={`w-full h-full object-cover transition-transform duration-500 ${selected ? 'scale-105' : 'group-hover:scale-105'}`}
          />
        </div>
      ) : (
        <div className="h-28 w-full bg-white/[0.03] flex items-center justify-center text-white/30">
          {renderIcon(type.icon, 'w-10 h-10')}
        </div>
      )}
      <div className={`px-3 py-2.5 ${selected ? 'bg-[#D9A05B]/12' : 'bg-white/[0.03]'}`}>
        <p className={`text-sm font-semibold ${selected ? 'text-[#D9A05B]' : 'text-white'}`}>{type.label}</p>
        <p className="text-[10.5px] text-white/45 mt-0.5 line-clamp-2">{type.description}</p>
      </div>
      {selected && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#D9A05B] flex items-center justify-center">
          <Check className="w-3.5 h-3.5 text-black" />
        </div>
      )}
    </button>
  );
};

// =====================================================================
// Main Wizard — flux guidé multi-étapes
// =====================================================================
const STEPS = [
  { key: 'identity', label: 'Identité' },
  { key: 'category', label: 'Catégorie' },
  { key: 'construction', label: 'Neuf / Existant' },
  { key: 'type', label: 'Type de projet' },
  { key: 'params', label: 'Paramétrage' },
  { key: 'mode', label: 'Mode d\u2019édition' },
  { key: 'recap', label: 'Récapitulatif' },
];

const NewProjectWizard = ({ open, onOpenChange, prefilledAddress = null }) => {
  const navigate = useNavigate();
  const { isEnabled } = useChannel();
  // Canal stable : le Mode Avancé (vectorisation) peut être masqué par flag
  const advancedAllowed = isEnabled('wizard_advanced_mode');
  const steps = advancedAllowed ? STEPS : STEPS.filter(s => s.key !== 'mode');
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [types, setTypes] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [prefilledCoords, setPrefilledCoords] = useState(null);
  const [category, setCategory] = useState(null); // 'residential' | 'tertiary'
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [pickedBuilding, setPickedBuilding] = useState(null);
  const [buildingCount, setBuildingCount] = useState(1);
  const [floorCount, setFloorCount] = useState(0);
  const [isNewConstruction, setIsNewConstruction] = useState(true);
  const [editionMode, setEditionMode] = useState(null); // 'basic' | 'advanced'
  const [apartmentFloor, setApartmentFloor] = useState('');
  const [apartmentNumber, setApartmentNumber] = useState('');
  const [aptLevels, setAptLevels] = useState(1);
  const [aptOutdoor, setAptOutdoor] = useState(false);
  const [houseFloorsAbove, setHouseFloorsAbove] = useState(1);
  const [houseBasements, setHouseBasements] = useState(0);
  const [houseAttic, setHouseAttic] = useState(true);
  const [houseAnnexes, setHouseAnnexes] = useState(0);
  const [houseOutdoor, setHouseOutdoor] = useState(true);
  const [mlBuildings, setMlBuildings] = useState(1);
  const [mlFloors, setMlFloors] = useState(3);
  const [mlLotsPerFloor, setMlLotsPerFloor] = useState(4);
  const [skipPicker, setSkipPicker] = useState(false);
  const [promptPreview, setPromptPreview] = useState(null);
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);

  const reset = () => {
    setStep(0); setName(''); setDescription(''); setAddress('');
    setPrefilledCoords(null); setCategory(null); setEditionMode(null);
    setSelectedTypeId(null); setPickedBuilding(null);
    setBuildingCount(1); setFloorCount(0); setIsNewConstruction(true);
    setApartmentFloor(''); setApartmentNumber('');
    setAptLevels(1); setAptOutdoor(false);
    setHouseFloorsAbove(1); setHouseBasements(0); setHouseAttic(true);
    setHouseAnnexes(0); setHouseOutdoor(true);
    setMlBuildings(1); setMlFloors(3); setMlLotsPerFloor(4);
    setSkipPicker(false);
    setPromptPreview(null); setPromptOpen(false);
  };

  useEffect(() => {
    if (!open) {
      const t = setTimeout(reset, 350);
      return () => clearTimeout(t);
    }
    axios.get(`${API}/project-types`, { withCredentials: true })
      .then(r => setTypes(r.data || []))
      .catch(() => toast.error('Impossible de charger les types de projet'));
    if (prefilledAddress?.display_name) {
      setAddress(prefilledAddress.display_name);
      if (typeof prefilledAddress.lat === 'number' && typeof prefilledAddress.lng === 'number') {
        setPrefilledCoords([prefilledAddress.lat, prefilledAddress.lng]);
      }
      const shortName = (prefilledAddress.display_name.split(',')[0] || '').trim();
      if (shortName && shortName.length <= 60) setName(shortName);
    }
  }, [open, prefilledAddress]);

  const allowedCodes = category === 'tertiary' ? TERTIARY_CODES : RESIDENTIAL_CODES;
  const filteredTypes = types.filter(t => !category || allowedCodes.includes(t.code));

  const selectedType = types.find(t => t.project_type_id === selectedTypeId);
  const needsPicker = selectedType?.requires_building_picker;
  const isApartment = selectedType?.code === 'apartment_single';
  const isHouse = selectedType?.code === 'house_single';
  const isMultiLotType = ['residence_single', 'residence_multi'].includes(selectedType?.code);
  const needsResidenceFields = selectedType?.requires_building_plans
    && !selectedType?.requires_building_picker && !isMultiLotType;

  const effMlBuildings = selectedType?.code === 'residence_single' ? 1 : mlBuildings;
  const structureConfig = (() => {
    if (isApartment) return { kind: 'apartment', levels: aptLevels, has_outdoor: aptOutdoor };
    if (isHouse) return {
      kind: 'house', floors_above: houseFloorsAbove, basements: houseBasements,
      has_attic: houseAttic, annex_buildings: houseAnnexes, has_outdoor: houseOutdoor,
    };
    if (isMultiLotType) return {
      kind: 'multi_lot', buildings: effMlBuildings,
      floors_per_building: mlFloors, lots_per_floor: mlLotsPerFloor,
    };
    return null;
  })();
  const structureSummary = (() => {
    if (isApartment) {
      const label = aptLevels === 1 ? 'Simplex' : aptLevels === 2 ? 'Duplex' : aptLevels === 3 ? 'Triplex' : `${aptLevels} niveaux`;
      return `${label} — ${aptLevels} niveau${aptLevels > 1 ? 'x' : ''}${aptOutdoor ? ' + terrain extérieur' : ''}`;
    }
    if (isHouse) {
      const parts = [];
      if (houseBasements) parts.push(`${houseBasements} sous-sol${houseBasements > 1 ? 's' : ''}`);
      parts.push('RDC');
      if (houseFloorsAbove) parts.push(`${houseFloorsAbove} étage${houseFloorsAbove > 1 ? 's' : ''}`);
      if (houseAttic) parts.push('combles');
      let s = parts.join(' + ');
      if (houseAnnexes) s += ` · ${houseAnnexes} annexe${houseAnnexes > 1 ? 's' : ''}`;
      if (houseOutdoor) s += ' · terrain';
      return s;
    }
    if (isMultiLotType) {
      return `${effMlBuildings} bât. × ${mlFloors} étage${mlFloors > 1 ? 's' : ''} × ${mlLotsPerFloor} lot${mlLotsPerFloor > 1 ? 's' : ''}/étage = ${effMlBuildings * mlFloors * mlLotsPerFloor} lots`;
    }
    return null;
  })();
  const skipSmartUpload = selectedType ? !selectedType.requires_building_plans : false;

  // Seed the picker with prefilled coords (drop-pin Hub flow)
  useEffect(() => {
    if (steps[step]?.key === 'params' && needsPicker && !pickedBuilding && prefilledCoords && address) {
      setPickedBuilding({ coords: prefilledCoords, address, map_provider: 'google_maps' });
    }
  }, [step, needsPicker, pickedBuilding, prefilledCoords, address]);

  // Reset selected type if category changes and it's no longer allowed
  useEffect(() => {
    if (category && selectedType && !allowedCodes.includes(selectedType.code)) {
      setSelectedTypeId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const stepKey = steps[step].key;

  // V14.72 — Aperçu du prompt IA au récap, dérivé des choix de typologie
  useEffect(() => {
    if (stepKey !== 'recap' || !selectedType) return;
    setPromptLoading(true);
    axios.post(`${API}/generation/wizard-preview-prompt`, {
      project_type_code: selectedType.code,
      is_new_construction: isNewConstruction,
      category,
      address: address.trim(),
      structure_config: structureConfig,
    }, { withCredentials: true })
      .then(r => setPromptPreview(r.data))
      .catch(() => setPromptPreview(null))
      .finally(() => setPromptLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  const canNext = (() => {
    if (stepKey === 'identity') return name.trim().length >= 2 && address.trim().length >= 3;
    if (stepKey === 'category') return !!category;
    if (stepKey === 'construction') return true;
    if (stepKey === 'type') return !!selectedTypeId;
    if (stepKey === 'params') {
      if (needsPicker) return !!pickedBuilding?.coords || skipPicker;
      return true;
    }
    if (stepKey === 'mode') return !!editionMode;
    return true;
  })();

  const doCreate = async (alsoUpload = false) => {
    setCreating(true);
    try {
      const generalInfo = {
        project_category: category,
        edition_mode: advancedAllowed ? (editionMode || 'basic') : 'basic',
      };
      if (isApartment) {
        if (apartmentFloor !== '') generalInfo.apartment_floor = apartmentFloor.trim();
        if (apartmentNumber !== '') generalInfo.apartment_number = apartmentNumber.trim();
      }
      const payload = {
        name: name.trim(),
        description: description.trim(),
        address: address.trim(),
        project_type_id: selectedTypeId,
        project_type_code: selectedType?.code,
        is_new_construction: isNewConstruction,
        ...(structureConfig ? { structure_config: structureConfig } : {}),
        ...(isMultiLotType ? { building_count: effMlBuildings, floor_count_estimate: mlFloors } : {}),
        ...(prefilledCoords ? { coords: prefilledCoords } : {}),
        ...(needsPicker && pickedBuilding ? { picked_building: pickedBuilding } : {}),
        ...(needsResidenceFields ? {
          building_count: buildingCount,
          floor_count_estimate: floorCount || null,
        } : {}),
        general_info: generalInfo,
      };
      const r = await axios.post(`${API}/projects`, payload, { withCredentials: true });
      toast.success(`Projet "${r.data.name}" créé`);
      onOpenChange(false);
      // Routage Basic vs Avancé — le mode avancé active l'Éditeur Plan V2
      // (vectorisation déterministe), le mode basic garde le flux legacy.
      try {
        localStorage.setItem('plan_editor_v2_default', editionMode === 'advanced' ? '1' : '0');
      } catch { /* no-op */ }
      if (editionMode === 'advanced') {
        navigate(`/projects/${r.data.project_id}`);
      } else if (alsoUpload && !skipSmartUpload) {
        navigate(`/hub/upload?project_id=${r.data.project_id}`);
      } else {
        navigate(`/projects/${r.data.project_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la création');
    } finally { setCreating(false); }
  };

  const renderTypeIcon = (iconName, className = 'w-5 h-5') => {
    const Icon = ICONS[iconName] || Box;
    return <Icon className={className} />;
  };

  const isLastStep = step === steps.length - 1;
  const progress = ((step + 1) / steps.length) * 100;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="hud-page-container !z-[2100] w-full sm:max-w-xl bg-[var(--c-surface-0)] border-[var(--c-border)] text-[var(--c-text)] p-0 overflow-y-auto"
        data-testid="new-project-wizard"
      >
        <SheetHeader className="px-6 py-5 border-b border-[var(--c-border)] sticky top-0 bg-[var(--c-surface-0)] backdrop-blur-xl z-20">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-white text-xl font-semibold flex items-center gap-2.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <div className="w-8 h-8 rounded-lg bg-[#D9A05B] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-black" />
              </div>
              Nouveau Projet
            </SheetTitle>
            <span className="text-[10px] uppercase tracking-widest font-bold text-white/40" data-testid="wizard-step-indicator">
              Étape {step + 1}/{steps.length}
            </span>
          </div>
          <SheetDescription className="text-white/45 text-xs mt-1">
            {steps[step].label} — l&rsquo;assistant vous guide pas à pas.
          </SheetDescription>

          {/* Barre de progression */}
          <div className="mt-4 h-1 rounded-full bg-white/8 overflow-hidden">
            <div
              className="h-full bg-[#D9A05B] rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1.5">
            {steps.map((s, i) => (
              <span
                key={s.key}
                className={`text-[8.5px] uppercase tracking-wider font-bold ${
                  i === step ? 'text-[#D9A05B]' : i < step ? 'text-white/50' : 'text-white/20'
                }`}
              >
                {i + 1}
              </span>
            ))}
          </div>
        </SheetHeader>

        <div className="px-6 py-6 space-y-5">
          {/* ============ IDENTITÉ ============ */}
          {stepKey === 'identity' && (
            <div className="space-y-4" data-testid="wizard-step-identity">
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-white/55 font-bold mb-1.5">Nom du projet *</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex. Résidence Auteuil"
                  className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-11 rounded-lg"
                  autoFocus
                  data-testid="wizard-name-input"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-white/55 font-bold mb-1.5">Localisation *</label>
                <div className="relative">
                  <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Adresse, ville, code postal"
                    className="bg-white/5 border-white/15 text-white placeholder:text-white/30 pl-9 h-11 rounded-lg"
                    data-testid="wizard-address-input"
                  />
                </div>
                <p className="text-[10px] text-white/30 mt-1">L'adresse sera géolocalisée automatiquement.</p>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-white/55 font-bold mb-1.5">Description (facultative)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 400))}
                  rows={3}
                  placeholder="Quelques mots sur le projet…"
                  className="w-full bg-white/5 border border-white/15 text-white placeholder:text-white/30 px-3 py-2 text-sm rounded-lg resize-none focus:outline-none focus:border-[#D9A05B]/40"
                  data-testid="wizard-description-input"
                />
              </div>
            </div>
          )}

          {/* ============ CATÉGORIE — RÉSIDENTIEL / TERTIAIRE ============ */}
          {stepKey === 'category' && (
            <div className="space-y-4" data-testid="wizard-step-category">
              <div>
                <p className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Quelle est la nature du projet ?
                </p>
                <p className="text-xs text-white/45 mt-1">Cela adapte les types de biens et les contraintes de génération IA.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <BigChoiceCard
                  selected={category === 'residential'}
                  onClick={() => setCategory('residential')}
                  icon={Home}
                  title="Résidentiel"
                  subtitle="Appartement, maison, immeuble d'habitation, résidence…"
                  testid="wizard-category-residential"
                />
                <BigChoiceCard
                  selected={category === 'tertiary'}
                  onClick={() => setCategory('tertiary')}
                  icon={Briefcase}
                  title="Tertiaire"
                  subtitle="Bureaux, locaux commerciaux, espaces professionnels…"
                  testid="wizard-category-tertiary"
                />
              </div>
            </div>
          )}

          {/* ============ NEUF / EXISTANT ============ */}
          {stepKey === 'construction' && (
            <div className="space-y-4" data-testid="wizard-step-construction">
              <div>
                <p className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Bien neuf ou existant ?
                </p>
                <p className="text-xs text-white/45 mt-1">Détermine si l'IA part d'une feuille blanche ou reproduit un bâti réel.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <BigChoiceCard
                  selected={isNewConstruction}
                  onClick={() => setIsNewConstruction(true)}
                  icon={Hammer}
                  title="Neuf"
                  subtitle="Programme neuf — génération from-scratch à partir des plans du promoteur."
                  testid="wizard-construction-new"
                />
                <BigChoiceCard
                  selected={!isNewConstruction}
                  onClick={() => setIsNewConstruction(false)}
                  icon={Landmark}
                  title="Existant"
                  subtitle="Bien existant — modélisation 3D fidèle au bâti réel."
                  testid="wizard-construction-existing"
                />
              </div>
            </div>
          )}

          {/* ============ TYPE DE PROJET (images) ============ */}
          {stepKey === 'type' && (
            <div className="space-y-4" data-testid="wizard-step-type">
              <div>
                <p className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Quel type de bien ?
                </p>
                <p className="text-xs text-white/45 mt-1">
                  {category === 'tertiary' ? 'Types tertiaires disponibles.' : 'Types résidentiels disponibles.'}
                </p>
              </div>
              {types.length === 0 ? (
                <div className="text-center py-8 text-white/40 text-sm flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {filteredTypes.map(t => (
                    <TypeImageCard
                      key={t.project_type_id}
                      type={t}
                      selected={selectedTypeId === t.project_type_id}
                      onClick={() => setSelectedTypeId(t.project_type_id)}
                      renderIcon={renderTypeIcon}
                    />
                  ))}
                </div>
              )}
              {selectedType && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {selectedType.requires_building_picker && (
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/25">Sélection bâtiment</span>
                  )}
                  {selectedType.requires_building_plans ? (
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/25">Plans requis</span>
                  ) : (
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">Sans plans complets</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ============ PARAMÉTRAGE (adaptatif) ============ */}
          {stepKey === 'params' && (
            <div className="space-y-4" data-testid="wizard-step-details">
              <div>
                <p className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Paramétrage {selectedType ? `— ${selectedType.label}` : ''}
                </p>
                <p className="text-xs text-white/45 mt-1">Champs adaptés au type de bien choisi.</p>
              </div>

              {isApartment && (
                <div className="space-y-2" data-testid="wizard-structure-apartment">
                  <label className="block text-[11px] uppercase tracking-widest text-white/55 font-bold mb-1">
                    Structure de l'appartement
                  </label>
                  <FieldRow label={`Niveaux (${aptLevels === 1 ? 'Simplex' : aptLevels === 2 ? 'Duplex' : aptLevels === 3 ? 'Triplex' : aptLevels + ' niveaux'})`}>
                    <NumStepper value={aptLevels} onChange={setAptLevels} min={1} max={5} testid="wizard-apt-levels" />
                  </FieldRow>
                  <ToggleChip
                    checked={aptOutdoor}
                    onChange={setAptOutdoor}
                    label="Terrain extérieur (rez-de-jardin, terrasse…)"
                    testid="wizard-apt-outdoor"
                  />
                  <p className="text-[10.5px] text-white/40">
                    {aptLevels} niveau{aptLevels > 1 ? 'x' : ''} — un plan par niveau sera préparé automatiquement.
                  </p>
                </div>
              )}

              {isHouse && (
                <div className="space-y-2" data-testid="wizard-structure-house">
                  <label className="block text-[11px] uppercase tracking-widest text-white/55 font-bold mb-1">
                    Structure de la maison
                  </label>
                  <FieldRow label="Étages (au-dessus du RDC)">
                    <NumStepper value={houseFloorsAbove} onChange={setHouseFloorsAbove} min={0} max={5} testid="wizard-house-floors" />
                  </FieldRow>
                  <FieldRow label="Sous-sols">
                    <NumStepper value={houseBasements} onChange={setHouseBasements} min={0} max={3} testid="wizard-house-basements" />
                  </FieldRow>
                  <FieldRow label="Bâtiments annexes (garage…)">
                    <NumStepper value={houseAnnexes} onChange={setHouseAnnexes} min={0} max={5} testid="wizard-house-annexes" />
                  </FieldRow>
                  <div className="flex items-center gap-2 flex-wrap">
                    <ToggleChip checked={houseAttic} onChange={setHouseAttic} label="Combles" testid="wizard-house-attic" />
                    <ToggleChip checked={houseOutdoor} onChange={setHouseOutdoor} label="Terrain extérieur" testid="wizard-house-outdoor" />
                  </div>
                  <p className="text-[10.5px] text-white/40" data-testid="wizard-house-summary">
                    Structure : {structureSummary}
                  </p>
                </div>
              )}

              {isMultiLotType && (
                <div className="space-y-2" data-testid="wizard-structure-multilot">
                  <label className="block text-[11px] uppercase tracking-widest text-white/55 font-bold mb-1">
                    Structure de la résidence
                  </label>
                  {selectedType?.code === 'residence_multi' && (
                    <FieldRow label="Bâtiments">
                      <NumStepper value={mlBuildings} onChange={setMlBuildings} min={1} max={12} testid="wizard-ml-buildings" />
                    </FieldRow>
                  )}
                  <FieldRow label="Étages par bâtiment">
                    <NumStepper value={mlFloors} onChange={setMlFloors} min={1} max={15} testid="wizard-ml-floors" />
                  </FieldRow>
                  <FieldRow label="Lots par étage">
                    <NumStepper value={mlLotsPerFloor} onChange={setMlLotsPerFloor} min={1} max={12} testid="wizard-ml-lots" />
                  </FieldRow>
                  <div className="bg-[#D9A05B]/8 border border-[#D9A05B]/25 rounded-lg px-3 py-2 text-xs text-[#D9A05B]" data-testid="wizard-ml-total">
                    {structureSummary}
                  </div>
                </div>
              )}

              {needsPicker && (
                <div data-testid="wizard-building-picker">
                  <label className="block text-[11px] uppercase tracking-widest text-white/55 font-bold mb-2">
                    Sélection du bâtiment
                  </label>
                  <p className="text-[11px] text-white/40 mb-3">
                    Cherchez l'adresse précise puis cliquez sur la carte pour pointer le bâtiment.
                  </p>
                  <BuildingPicker
                    value={pickedBuilding}
                    onChange={setPickedBuilding}
                    initialSearch={address}
                  />
                  {!pickedBuilding?.coords && (
                    <button
                      type="button"
                      onClick={() => setSkipPicker((v) => !v)}
                      className={`mt-2 text-[11px] underline underline-offset-2 transition-colors ${
                        skipPicker ? 'text-[#D9A05B]' : 'text-white/45 hover:text-white/70'
                      }`}
                      data-testid="wizard-skip-picker-btn"
                    >
                      {skipPicker
                        ? '✓ Sélection ignorée — l\u2019adresse suffira (cliquez pour annuler)'
                        : 'Ignorer la sélection cartographique (l\u2019adresse suffit)'}
                    </button>
                  )}
                </div>
              )}

              {isApartment && (
                <div className="grid grid-cols-2 gap-3" data-testid="wizard-apartment-fields">
                  <div>
                    <label className="block text-[11px] uppercase tracking-widest text-white/55 font-bold mb-1.5">
                      Étage <span className="text-white/30 normal-case font-normal text-[10px]">(facultatif)</span>
                    </label>
                    <Input
                      value={apartmentFloor}
                      onChange={(e) => setApartmentFloor(e.target.value)}
                      placeholder="Ex : 3, RDC, R+2…"
                      className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-10 rounded-lg"
                      data-testid="wizard-apartment-floor-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-widest text-white/55 font-bold mb-1.5">
                      N° d'appartement / lot <span className="text-white/30 normal-case font-normal text-[10px]">(facultatif)</span>
                    </label>
                    <Input
                      value={apartmentNumber}
                      onChange={(e) => setApartmentNumber(e.target.value)}
                      placeholder="Ex : A12, Lot 7B…"
                      className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-10 rounded-lg"
                      data-testid="wizard-apartment-number-input"
                    />
                  </div>
                  <p className="col-span-2 text-[10.5px] text-white/40 leading-relaxed -mt-1">
                    La localisation 3D précise dans l'immeuble pourra être ajustée depuis la fiche projet.
                  </p>
                </div>
              )}

              {needsResidenceFields && (
                <div className="space-y-3" data-testid="wizard-residence-fields">
                  <p className="text-[11px] text-white/40">
                    Champs facultatifs — vous pourrez les compléter à l'upload des plans.
                  </p>
                  <FieldRow label="Nombre de bâtiments">
                    <NumStepper value={buildingCount} onChange={setBuildingCount} min={1} max={50} testid="wizard-buildings" />
                  </FieldRow>
                  <div>
                    <label className="block text-[11px] uppercase tracking-widest text-white/55 font-bold mb-1.5">
                      Étages estimés (par bâtiment) — facultatif
                    </label>
                    <Input
                      type="number"
                      min="0" max="100"
                      value={floorCount || ''}
                      onChange={(e) => setFloorCount(parseInt(e.target.value) || 0)}
                      placeholder="0 = je ne sais pas encore"
                      className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-10 rounded-lg"
                      data-testid="wizard-floors-input"
                    />
                  </div>
                </div>
              )}

              {!isApartment && !isHouse && !isMultiLotType && !needsPicker && !needsResidenceFields && (
                <p className="text-sm text-white/45 py-4 text-center">
                  Aucun paramétrage supplémentaire requis pour ce type — passez à l'étape suivante.
                </p>
              )}
            </div>
          )}

          {/* ============ MODE — BASIC / AVANCÉ ============ */}
          {stepKey === 'mode' && (
            <div className="space-y-4" data-testid="wizard-step-mode">
              <div>
                <p className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Comment souhaitez-vous travailler ?
                </p>
                <p className="text-xs text-white/45 mt-1">Vous pourrez changer de mode à tout moment depuis la fiche projet.</p>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {[
                  {
                    key: 'basic',
                    icon: Wand2,
                    title: 'Mode Basic',
                    badge: 'Simple & rapide',
                    desc: 'Uploadez vos documents (plans, photos…) et lancez directement la génération IA. Idéal pour un rendu rapide sans retouche géométrique.',
                  },
                  {
                    key: 'advanced',
                    icon: PenTool,
                    title: 'Mode Avancé',
                    badge: 'Vectorisation précise',
                    desc: 'Vectorisation déterministe du plan + Éditeur Plan 2D/3D : murs, cotes, pièces m², ouvertures. Contrôle total de la géométrie micro-IFC avant génération.',
                  },
                ].map(m => (
                  <button
                    key={m.key}
                    onClick={() => setEditionMode(m.key)}
                    className={`relative rounded-2xl border overflow-hidden text-left transition-all flex items-stretch ${
                      editionMode === m.key
                        ? 'border-[#D9A05B]/60 shadow-[0_0_30px_rgba(217,160,91,0.15)]'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`wizard-mode-${m.key}`}
                  >
                    <div className="w-28 shrink-0 bg-black">
                      <img src={MODE_IMAGES[m.key]} alt={m.title} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <div className={`flex-1 px-4 py-3.5 ${editionMode === m.key ? 'bg-[#D9A05B]/10' : 'bg-white/[0.03]'}`}>
                      <div className="flex items-center gap-2">
                        <m.icon className={`w-4 h-4 ${editionMode === m.key ? 'text-[#D9A05B]' : 'text-white/55'}`} />
                        <p className={`text-sm font-semibold ${editionMode === m.key ? 'text-[#D9A05B]' : 'text-white'}`}>{m.title}</p>
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-white/8 text-white/50 border border-white/10">{m.badge}</span>
                      </div>
                      <p className="text-[11px] text-white/50 mt-1.5 leading-relaxed">{m.desc}</p>
                    </div>
                    {editionMode === m.key && (
                      <div className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-[#D9A05B] flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-black" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ============ RÉCAP ============ */}
          {stepKey === 'recap' && (
            <div className="space-y-3" data-testid="wizard-step-recap">
              <p className="text-[11px] uppercase tracking-widest text-white/55 font-bold mb-2">Récapitulatif</p>

              {[
                ['Nom', name],
                ['Localisation', address],
                ['Catégorie', category === 'tertiary' ? 'Tertiaire' : 'Résidentiel'],
                ['Construction', isNewConstruction ? 'Neuf' : 'Existant'],
                ['Type', selectedType?.label],
                ...(structureSummary ? [['Structure', structureSummary]] : []),
                ['Mode', editionMode === 'advanced' ? 'Avancé — vectorisation précise' : 'Basic — simple & rapide'],
                ...(needsResidenceFields ? [
                  ['Bâtiments', buildingCount],
                  ['Étages estimés', floorCount > 0 ? floorCount : '—'],
                ] : []),
                ...(isApartment && (apartmentFloor || apartmentNumber) ? [
                  ['Localisation logement', [
                    apartmentFloor && `Étage ${apartmentFloor}`,
                    apartmentNumber && `N° ${apartmentNumber}`,
                  ].filter(Boolean).join(' · ')],
                ] : []),
                ...(needsPicker && pickedBuilding && (
                  (pickedBuilding.address && pickedBuilding.address.trim() !== address.trim()) ||
                  (prefilledCoords && pickedBuilding.coords && (
                    Math.abs(pickedBuilding.coords[0] - prefilledCoords[0]) > 0.00005 ||
                    Math.abs(pickedBuilding.coords[1] - prefilledCoords[1]) > 0.00005
                  ))
                ) ? [
                  ['Bâtiment ciblé', pickedBuilding.address || `${pickedBuilding.coords[0].toFixed(5)}, ${pickedBuilding.coords[1].toFixed(5)}`],
                ] : []),
                ...(description ? [['Description', description]] : []),
              ].map(([k, v]) => (
                <div key={k} className="flex items-start gap-3 px-3 py-2 bg-white/[0.03] border border-white/10 rounded-lg">
                  <p className="text-[10px] uppercase tracking-widest text-white/40 font-bold w-24 shrink-0 pt-0.5">{k}</p>
                  <p className="text-sm text-white flex-1 break-words">{v}</p>
                </div>
              ))}

              {/* V14.72 — Aperçu du prompt IA (impact des choix de typologie) */}
              <div className="rounded-lg border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="wizard-prompt-preview">
                <button
                  type="button"
                  onClick={() => setPromptOpen(v => !v)}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-white/[0.04]"
                  data-testid="wizard-prompt-preview-toggle"
                >
                  <span className="text-xs font-semibold text-white flex items-center gap-1.5">
                    <Wand2 className="w-3.5 h-3.5 text-[#D9A05B]" />
                    Aperçu du prompt IA
                    {promptLoading && <Loader2 className="w-3 h-3 animate-spin text-white/40" />}
                    {!promptLoading && promptPreview && (
                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-white/8 text-white/45 border border-white/10">
                        {promptPreview.prompt_chars} car.
                      </span>
                    )}
                  </span>
                  <ChevronRight className={`w-3.5 h-3.5 text-white/40 transition-transform ${promptOpen ? 'rotate-90' : ''}`} />
                </button>
                {promptOpen && (
                  <div className="px-3 pb-3">
                    {promptLoading ? (
                      <p className="text-[11px] text-white/40 py-2">Génération de l'aperçu…</p>
                    ) : promptPreview?.prompt ? (
                      <>
                        <pre className="text-[10px] leading-relaxed text-white/65 bg-black/40 border border-white/10 rounded-md p-2.5 max-h-52 overflow-y-auto whitespace-pre-wrap font-mono" data-testid="wizard-prompt-preview-text">
                          {promptPreview.prompt}
                        </pre>
                        <p className="text-[10px] text-white/35 mt-1.5 leading-relaxed">{promptPreview.note}</p>
                      </>
                    ) : (
                      <p className="text-[11px] text-white/40 py-2">Aperçu indisponible pour ce type de projet.</p>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-[#D9A05B]/8 border border-[#D9A05B]/25 rounded-lg p-3 mt-4">
                <p className="text-xs text-[#D9A05B] font-semibold flex items-center gap-1.5 mb-1">
                  <Sparkles className="w-3.5 h-3.5" />
                  Étape suivante
                </p>
                <p className="text-[11px] text-white/55 leading-relaxed">
                  {editionMode === 'advanced'
                    ? "Vous arrivez directement sur la fiche projet avec l'Éditeur Plan Avancé activé — vectorisez votre plan puis affinez la géométrie en 2D/3D."
                    : structureConfig
                    ? "La structure (bâtiments · étages · lots) sera créée automatiquement — vous arrivez directement sur la fiche projet, prête pour l'Édition Plan."
                    : skipSmartUpload ? (
                    isApartment
                      ? "Vous serez redirigé(e) vers la fiche projet où vous pourrez ajouter le plan de l'appartement et préciser sa localisation 3D dans le bâtiment."
                      : "Vous serez redirigé(e) vers la fiche projet où vous pourrez ajouter les pièces (plan, photos…) à tout moment."
                  ) : (
                    "Le projet sera créé immédiatement. Vous pourrez ajouter les documents (plans masse, carnet de détail, etc.) plus tard depuis la fiche projet."
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 px-6 py-4 border-t border-[var(--c-border)] bg-[var(--c-surface-0)] backdrop-blur-xl flex items-center justify-between gap-2">
          <button
            onClick={() => step === 0 ? onOpenChange(false) : setStep(step - 1)}
            disabled={creating}
            className="text-xs text-white/55 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-40"
            data-testid="wizard-back-btn"
          >
            {step === 0 ? <><X className="w-3.5 h-3.5" /> Annuler</> : <><ChevronLeft className="w-3.5 h-3.5" /> Retour</>}
          </button>

          {!isLastStep ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canNext || creating}
              data-testid="wizard-next-btn"
              className="bg-[#D9A05B] hover:bg-[#C78D46] text-black font-medium h-10 px-5 rounded-lg disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              Suivant <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          ) : (editionMode === 'advanced' || skipSmartUpload) ? (
            <Button
              onClick={() => doCreate(false)}
              disabled={creating}
              data-testid="wizard-create-btn"
              className="bg-[#D9A05B] hover:bg-[#C78D46] text-black font-medium h-10 px-5 rounded-lg disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5" /> Créer le projet</>}
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => doCreate(false)}
                disabled={creating}
                data-testid="wizard-create-btn"
                className="bg-white/5 border-white/15 text-white hover:bg-white/10 h-10 px-4 rounded-lg disabled:opacity-40"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Créer'}
              </Button>
              <Button
                onClick={() => doCreate(true)}
                disabled={creating}
                data-testid="wizard-create-and-upload-btn"
                className="bg-[#D9A05B] hover:bg-[#C78D46] text-black font-medium h-10 px-5 rounded-lg disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5" /> Créer + Uploader</>}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default NewProjectWizard;
